import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import enUI from '@linktivity/link-ui/lang/en';
import jaUI from '@linktivity/link-ui/lang/ja';
import koUI from '@linktivity/link-ui/lang/ko';
import zhUI from '@linktivity/link-ui/lang/zh';
import ztUI from '@linktivity/link-ui/lang/zt';
import { JAPANESE_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import { LocaleOption } from '@link/share/services/types';
import enCountry from './country/en';
import jaCountry from './country/ja';
import koCountry from './country/ko';
import zhCountry from './country/zh';
import ztCountry from './country/zt';
import { deepMergeResources, type DeepPartial } from './deepMerge';
import enJapanPrefecture from './japanPrefectures/en';
import jaJapanPrefecture from './japanPrefectures/ja';
import koJapanPrefecture from './japanPrefectures/ko';
import zhJapanPrefecture from './japanPrefectures/zh';
import ztJapanPrefecture from './japanPrefectures/zt';
import shareEnText from './lang/en';
import shareJaText from './lang/ja';
import shareKoText from './lang/ko';
import shareZhText from './lang/zh';
import shareZtText from './lang/zt';
import enLang from './language/en';
import jaLang from './language/ja';
import koLang from './language/ko';
import zhLang from './language/zh';
import ztLang from './language/zt';

export const DEFAULT_LOCALE = JAPANESE_LOCALE;
export const LOCALE_LIST: string[] = LOCALE_OPTIONS.map(locale => locale.value);

export const getLocaleValues = (options: readonly LocaleOption[]): string[] =>
  options.map(locale => locale.value);

interface ResourcesRecord {
  [key: string]: string | string[] | ResourcesRecord | Record<string, unknown>;
}

/**
 * The share-side tree an app may override, per locale: the shared lang file,
 * link-ui's text and the three standalone namespaces (see `translation()`).
 */
type ShareResourceTree = typeof shareEnText &
  typeof enUI & {
    country: typeof enCountry;
    prefecture: typeof enJapanPrefecture;
    lang: typeof enLang;
  };

export type Locale = (typeof LOCALE_OPTIONS)[number]['value'];

/**
 * Per locale and partial: an app may override some locales only. Covering
 * every locale is a review concern the type cannot meaningfully force.
 */
export type LocaleOverrides = Partial<
  Record<Locale, DeepPartial<ShareResourceTree>>
>;

/**
 * Pins an app's overrides to the shared resource shape, so a mistyped key path
 * fails to compile instead of silently never resolving.
 */
export const defineLocaleOverrides = (
  overrides: LocaleOverrides
): LocaleOverrides => overrides;

interface Resources {
  jaText?: ResourcesRecord;
  enText?: ResourcesRecord;
  koText?: ResourcesRecord;
  zhText?: ResourcesRecord;
  ztText?: ResourcesRecord;
  /** Deep-merged last, so it wins over share's text and the app's `xxText`. */
  overrides?: LocaleOverrides;
}

interface ShareLocaleText {
  text: ResourcesRecord;
  ui: ResourcesRecord;
  country: ResourcesRecord;
  prefecture: ResourcesRecord;
  lang: ResourcesRecord;
}

const SHARE_TEXT: Record<Locale, ShareLocaleText> = {
  ja: {
    text: shareJaText,
    ui: jaUI,
    country: jaCountry,
    prefecture: jaJapanPrefecture,
    lang: jaLang
  },
  en: {
    text: shareEnText,
    ui: enUI,
    country: enCountry,
    prefecture: enJapanPrefecture,
    lang: enLang
  },
  ko: {
    text: shareKoText,
    ui: koUI,
    country: koCountry,
    prefecture: koJapanPrefecture,
    lang: koLang
  },
  zh: {
    text: shareZhText,
    ui: zhUI,
    country: zhCountry,
    prefecture: zhJapanPrefecture,
    lang: zhLang
  },
  zt: {
    text: shareZtText,
    ui: ztUI,
    country: ztCountry,
    prefecture: ztJapanPrefecture,
    lang: ztLang
  }
};

// Later layers win: share's text, the app's text, link-ui's text, the
// standalone namespaces, then the app's overrides.
const translation = (
  locale: Locale,
  appText?: ResourcesRecord,
  overrides?: LocaleOverrides
): ResourcesRecord => {
  const { text, ui, country, prefecture, lang } = SHARE_TEXT[locale];
  return deepMergeResources<ResourcesRecord>(
    { ...text, ...appText, ...ui, country, prefecture, lang },
    overrides?.[locale]
  );
};

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

export const resources = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  overrides
}: Resources) => ({
  ja: { translation: translation('ja', jaText, overrides) },
  en: { translation: translation('en', enText, overrides) },
  ko: { translation: translation('ko', koText, overrides) },
  zh: { translation: translation('zh', zhText, overrides) },
  zt: { translation: translation('zt', ztText, overrides) }
});

export function setLocale(locale: string): string {
  void i18n.changeLanguage(locale);
  document.documentElement.setAttribute('lang', locale);
  localStorage.setItem('locale', locale);
  return locale;
}

function getLocaleParams(supportedLocales: string[]) {
  const searchParams = new URLSearchParams(location.search);
  const lang = searchParams.get('lang') || '';
  if (supportedLocales.includes(lang)) {
    return lang;
  }
}

interface InitI18nOptions extends Resources {
  defaultLocale?: string;
  supportedLocales?: string[];
}

export const initI18n = ({
  jaText,
  enText,
  koText,
  zhText,
  ztText,
  overrides,
  defaultLocale = DEFAULT_LOCALE,
  supportedLocales = LOCALE_LIST
}: InitI18nOptions) => {
  void i18n.use(initReactI18next).init({
    lng: defaultLocale,
    returnNull: false,
    // React escapes text nodes already; i18next's escaping would turn an
    // interpolated "9/15 (Mon)" into "9&#x2F;15 (Mon)".
    interpolation: {
      escapeValue: false
    },
    resources: resources({
      jaText,
      enText,
      koText,
      zhText,
      ztText,
      overrides
    })
  });

  const resolvedLocale =
    getLocaleParams(supportedLocales) ||
    localStorage.getItem('locale') ||
    defaultLocale;
  setLocale(
    supportedLocales.includes(resolvedLocale) ? resolvedLocale : defaultLocale
  );
};

export default i18n;
